import React from "react";
import Project from "../types/Project";
import ProjectMailFolder from "../types/ProjectMailFolder";
import {appVersionString} from "../config";


export class PluginApi
{
    constructor(private _baseUrl: string, private getAccessToken: () => Promise<string>) {
    }

    private async fetch(method: string, url: string, bodyData: any | undefined = undefined, 
                        abortSignal: AbortSignal|undefined = undefined): Promise<Response>
    {
        const body = bodyData ? JSON.stringify(bodyData) : undefined;
        const token = await this.getAccessToken();
        const absoluteUrl = `${this._baseUrl}/${url}`;
        return fetch(
            absoluteUrl,
            {
                method: method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: body,
                signal: abortSignal
            }
        );
    }

    public async translateToImmutableRestIds(mutableRestIds: string[]) : Promise<string[]> {
        type BodyType = {
            mutableRestIds: string[],
        };

        const body: BodyType = {
            mutableRestIds
        };

        const result = await this.fetch("post", "email/translateIds", body)
            .then(async (response): Promise<string[]> => {
                if (response.ok)
                    return await response.json();
                return [];
            }).catch((e): string[] => {
                console.error("project API call failed", e);
                return [];
            });

        return result;
    }
    
    public async getInternetMessageIds(immutableRestIds: string[]): Promise<string[]>
    {
        const empty: { [key: string]: string } = {};
        const mapping = await this
            .fetch("post", "email/internetMessageIds", immutableRestIds)
            .then(async (response): Promise<Record<string, string>> => {
                if (response.ok)
                    return await response.json();
                return empty;
            }).catch((e): Record<string, string> => {
                console.error("getInternetMessageIds API call failed", e);
                return empty;
            });
        
        return immutableRestIds.map(id => mapping[id]);
    }
    
    public async registerOpen()
    {
        const body = {
            "version": appVersionString
        };
        await this.fetch("post", "telemetry/taskPaneOpened", body);
    }

    public async searchProjects(search: string, count: number,
                                abortSignal: AbortSignal | undefined = undefined): Promise<ProjectSearchResponse> {
        type SearchProjectsRequestBody = {
            searchString: string,
            count: number
        }

        const body: SearchProjectsRequestBody = {
            searchString: search,
            count: count
        }

        return this.fetch("post",`project/search`, body, abortSignal)
            .then(async (response): Promise<ProjectSearchResponse> => {
                let projects: Project[] = [];
                if (response.ok)
                    projects = await response.json();
                return {aborted: false, projects: projects};
            }).catch(e => {
                const projects: Project[] = [];
                let aborted = abortSignal.aborted;
                if (!aborted)
                    console.error("project API call failed", e);
                return {aborted: aborted, projects: projects};
            });
    }

    public async projects(internetMessageIds: string[]): Promise<ProjectsResponse> {
        const empty: ProjectsResponse = {filed: []};
        return this.fetch("post", `email/projects`, {internetMessageIds: internetMessageIds})
            .then(async (response): Promise<ProjectsResponse> => {
                if (response.ok)
                    return await response.json();
                return empty;
            }).catch(e => {
                console.error("projects API call failed", e);
                throw e;
            });
    }

    public async pinned(): Promise<Project[]> {
        return this.fetch("get", `email/projects/pinned`)
            .then(response => {
                if (response.ok)
                    return response.json()
                else
                    return []
            }).catch(e => {
                console.error("pinned API called failed", e);
                return [];
            })
    }
    
    public async recentUnpinned(count: number): Promise<Project[]> {
        return this.fetch("get", `email/projects/recentUnpinned?count=${count}`)
            .then(response => {
                if (response.ok)
                    return response.json()
                else
                    return []
            }).catch(e => {
                console.error("recent API called failed", e);
                return [];
            })
    }
    
    public async TEMP_EnsureWeHaveInternetMessageIdsMutableRestIds(mutableRestIds: string[], internetMessageIds: string[] ): Promise<string[]>
    {
        if (internetMessageIds.some(s => s === null || s.trim().length === 0))
        {
            const immutableRestIds = await this.translateToImmutableRestIds(mutableRestIds);
            internetMessageIds = await this.getInternetMessageIds(immutableRestIds);
        }
        return internetMessageIds
    }

    public async file(projectId: string, mutableRestIds: string[], internetMessageIds: string[]): Promise<boolean> {
        const immutableRestIds = await this.translateToImmutableRestIds(mutableRestIds);
        return this.fetch("post", "email/file", {
            projectId: projectId,
            immutableRestIds: immutableRestIds,
            internetMessageIds: internetMessageIds
        })
            .then((response) => response.ok)
            .catch(e => {
                console.error("fileToProject API call failed", e);
                return false;
            });
    }

    public async unfile(projectId: string, mutableRestIds: string[], internetMessageIds: string[]): Promise<boolean> {
        const immutableRestIds = await this.translateToImmutableRestIds(mutableRestIds);
        return this.fetch("post", "email/unfile", {
            projectId: projectId,
            immutableRestIds: immutableRestIds,
            internetMessageIds: internetMessageIds
        })
            .then((response) => response.ok)
            .catch(e => {
                console.error("unfileFromProject API call failed", e);
                return false;
            });
    }

    public async fileWithJobNumber(jobNumber: string, mutableRestIds: string[], internetMessageIds: string[]): Promise<boolean> {
        const immutableRestIds = await this.translateToImmutableRestIds(mutableRestIds);
        return this.fetch("post", "email/fileWithJobNumber", {
            jobNumber,
            immutableRestIds: immutableRestIds,
            internetMessageIds: internetMessageIds
        })
            .then(r => r.ok)
            .catch(e => {
                console.error("fileWithProjectNumber API call failed", e);
                return false;
            });
    }

    public async pin(projectId: string): Promise<boolean> {
        return this.fetch("post", "project/pin", projectId)
            .then((response) => response.ok)
            .catch(e => {
                console.error("pinProject API call failed", e);
                return false;
            });
    }

    public async unpin(projectId: string): Promise<boolean> {
        return this.fetch("post", "project/unpin", projectId)
            .then((response) => response.ok)
            .catch(e => {
                console.error("pinProject API call failed", e);
                return false;
            });
    }

    public async projectMailFolder(mutableRestId: string): Promise<ProjectMailFolder> {
        const emptyProjectMailFolder = {id: "", project: undefined, tracked: false};
        if (!mutableRestId)
            return emptyProjectMailFolder;

        const immutableRestIds = await this.translateToImmutableRestIds([mutableRestId]);
        const immutableRestId = immutableRestIds[0];
        if (!immutableRestId)
            return emptyProjectMailFolder;

        return this.fetch("get", `email/${immutableRestId}/projectMailFolder`)
            .then(response => response.json())
            .catch(e => {
                console.error("projectMailFolder API call failed", e);
                throw e;
            });
    }

    public async trackMailFolder(folderId: string) {
        return this.fetch("post", `mailFolder/subscription`, folderId)
            .then(response => response.ok)
            .catch(e => {
                console.error("trackMailFolder API call failed", e);
                throw e;
            })
    }

    public async untrackMailFolder(folderId: string) {
        return this.fetch("delete", `mailFolder/subscription`, folderId)
            .then(response => response.ok)
            .catch(e => {
                console.error("untrackMailFolder API call failed", e);
                throw e;
            })
    }

    public async fileEmailsInFolder(folderId: string) {
        return this.fetch("post", `mailFolder/file`, folderId)
            .then(response => response.ok)
            .catch(e => {
                console.error("fileEmailsInFolder API call failed", e);
                throw e;
            })
    }
    
    public async bulkFileEmailsInFolder(folderId: string, projectId: string) {
        return this.fetch("post", "mailFolder/file", {folderId: folderId, projectId: projectId})
            .then(response => response.ok)
            .catch(e => {
                console.error("bulkFileEmailsInFolder API call failed", e);
            });
    }

    public async getRecentlyFiledProjects(count: number): Promise<Project[]> {
        return this.fetch("get", `email/recentlyFiled?count=${count}`)
            .then(response => {
                if (response.ok)
                    return response.json()
                else
                    return []
            }).catch(e => {
                console.error("recentlyFiled API called failed", e);
                return [];
            })
    }
    
    public async exchangeForGraphToken(scopes: string[]): Promise<ExchangeForGraphTokenResponse | undefined> {
        return this.fetch("post", `exchangeForGraphToken`, scopes)
            .then(async response => {
                if (response.ok) {
                    return await response.json();
                } else
                    return undefined;        
            }).catch(e => {
                console.error("exchangeForGraphToken API called failed", e);
                return [];
            });
    }
}

export interface ProjectsResponse {
    filed: Project[];
}

export interface ProjectSearchResponse {
    aborted: boolean;
    projects: Project[];
}

export interface ExchangeForGraphTokenResponse {
    accessToken: string; 
    expiryTime: Date
    refreshTime: Date
}

export const PluginApiContext = React.createContext<PluginApi>(null);
